import React from "react";
import { TrendingUp, TrendingDown } from "lucide-react";
import {
  LineChart,
  Line,
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";
import GenericDonutChart from "../../components/charts/GenericDonutChart";
import GenericAreaChart from "../../components/charts/GenericAreaChart";

interface Props {
  data: any;
}

const OverviewTab: React.FC<Props> = ({ data }) => {
  const revenueTrendData = data?.revenue_trend?.map((item: any) => ({
    day: item.date,
    value: item.revenue,
  }));

  const chargerStatusData = [
    {
      name: `Online (${data?.charger_status?.online})`,
      value: data?.charger_status?.online,
      color: "#10b981",
    },
    {
      name: `Offline (${data?.charger_status?.offline})`,
      value: data?.charger_status?.offline,
      color: "#ef4444",
    },
    {
      name: `Maintenance (${data?.charger_status?.maintenance})`,
      value: data?.charger_status?.maintenance,
      color: "#fbbf24",
    },
  ];

  const hourlyUsageData = data?.hourly_usage?.map((item: any) => ({
    time: new Date(item.timestamp).getHours() + ":00",
    value: item.usage_percent,
  }));

  const formatDuration = (totalMinutes: number) => {
    if (!totalMinutes || totalMinutes <= 0) return "0m";

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours > 0 && minutes > 0) return `${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h`;
    return `${minutes}m`;
  };
  const getChangeClass = (value: number | null | undefined) => {
    if (!value) return "";
    if (value > 0) return "positive";
    if (value < 0) return "negative";
    return "neutral";
  };

  return (
    <>
      <div className="metrics-grid">
        <div className="metric-card">
          <span className="metric-label">Total earnings</span>
          <div className="metric-value">
            ₹{data?.metrics?.total_earnings?.value?.toFixed(2)}
          </div>
          <div
            className={`metric-change ${getChangeClass(
              data?.metrics?.total_earnings?.change_percent
            )}`}
          >
            {data?.metrics?.total_earnings?.change_percent > 0 ? (
              <TrendingUp size={14} />
            ) : (
              <TrendingDown size={14} />
            )}
            {data?.metrics?.total_earnings?.change_percent ?? 0}% change
          </div>

          <ResponsiveContainer width="100%" height={40}>
            <LineChart data={revenueTrendData}>
              <Line
                type="monotone"
                dataKey="value"
                stroke="#10b981"
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="metric-card">
          <span className="metric-label">Active chargers</span>
          <div className="metric-value">
            {data?.metrics?.active_chargers?.active}/
            {data?.metrics?.active_chargers?.total}
          </div>
          <div className="metric-status">
            {data?.metrics?.active_chargers?.health_percent}% health
          </div>
        </div>

        <div className="metric-card">
          <span className="metric-label">Bookings</span>
          <div className="metric-value">
            {data?.metrics?.bookings_today?.value}
          </div>
          <ResponsiveContainer width="100%" height={40}>
            <AreaChart data={revenueTrendData}>
              <Area
                type="monotone"
                dataKey="value"
                stroke="#10b981"
                fill="#d1fae5"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="metric-card">
          <span className="metric-label">Energy consumed</span>
          <div className="metric-value">
            {data?.metrics?.energy_consumed?.value} kWh
          </div>
          <div
            className={`metric-change ${getChangeClass(
              data?.metrics?.energy_consumed?.change_percent
            )}`}
          >
            {data?.metrics?.energy_consumed?.change_percent > 0 ? (
              <TrendingUp size={14} />
            ) : (
              <TrendingDown size={14} />
            )}
            {data?.metrics?.energy_consumed?.change_percent?.toFixed(2)}%
          </div>
        </div>
      </div>

      <div className="charts-grid">
        <GenericAreaChart
          data={revenueTrendData}
          title="Revenue trend"
          xAxisKey="day"
          yAxisKey="value"
          color="#10b981"
          height={300}
        />
        <GenericDonutChart data={chargerStatusData} title="Charger status" />
      </div>

      <GenericAreaChart
        data={hourlyUsageData}
        title="Hourly usage"
        xAxisKey="time"
        yAxisKey="value"
        color="#8b5cf6"
        height={250}
        yAxisFormatter={(v) => `${v}%`}
        fullWidth
      />
      <div className="bottom-grid">
        <div className="sessions-card">
          <h3>Recent sessions</h3>
          <div
            className="sessions-table scrollable-list"
            style={{ maxHeight: "400px", overflowY: "auto" }}
          >
            {data?.recent_sessions?.map((session: any, index: number) => (
              <div key={index} className="session-item">
                <div className="session-left">
                  <span
                    className={`status-badge-small ${session.status.toLowerCase()}`}
                  >
                    {session.status}
                  </span>
                  <div>
                    <div className="session-name">{session.user_name}</div>
                    <div className="session-details">
                      {formatDuration(session.duration_minutes)} |{" "}
                      {session.energy_kwh} kWh
                    </div>
                  </div>
                </div>
                <div>
                  <div className="session-amount">₹{session.amount}</div>
                  <div className="session-currency">{session.currency}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* <div className="faults-card">
          <h3>Recent faults</h3>
          <div>
            {data.recent_faults?.map((fault: any, index: number) => (
              <div key={index} className="fault-item">
                <div className="fault-left">
                  <div>
                    <div className="fault-charger">{fault.charger_id}</div>
                    <div className="fault-error">{fault.error_message}</div>
                  </div>
                </div>
                <div className="fault-right">
                  <div className="fault-time">{fault.time_ago}</div>
                  <span
                    className={`severity-badge ${fault.severity.toLowerCase()}`}
                  >
                    {fault.severity}
                  </span>
                  <button className="view-details">View details</button>
                </div>
              </div>
            ))}
          </div>
        </div> */}
      </div>
    </>
  );
};

export default OverviewTab;
